import type { AuditActionGroup, AuditActorType, AuditHiddenReason } from '../contracts/audit';
import { TASK_FIELD_LABELS, TASK_HISTORY_ACTION_LABELS } from './work';

/**
 * Historia zmian (audit_log → „Historia”): etykiety akcji i pól po polsku. Używa ich usługa `audit.listForObject`
 * (serwer) i komponent HistoryList (web). Pola spoza listy nie są pokazywane (np. id, wersja, znaczniki czasu).
 */

export const HISTORY_ACTION_LABELS: Record<string, string> = {
  ...TASK_HISTORY_ACTION_LABELS,
  'client.create': 'Utworzono klienta',
  'client.update': 'Zmieniono dane klienta',
  'client.archive': 'Przeniesiono klienta do archiwum',
  'client.restore': 'Przywrócono klienta z archiwum',
  'client.set_owner': 'Zmieniono opiekuna',
  'client.registry_apply': 'Uzupełniono dane firmy z rejestru',
  'company_registry.lookup': 'Sprawdzono NIP w rejestrze firm',
  'contact.create': 'Dodano kontakt',
  'contact.update': 'Zmieniono kontakt',
  'contact.archive': 'Przeniesiono kontakt do archiwum',
  'contact.restore': 'Przywrócono kontakt',
  'contact.set_primary': 'Ustawiono kontakt główny',
  'contact.conflict.resolve': 'Rozstrzygnięto konflikt adresu email',
  'project.create': 'Utworzono projekt',
  'project.update': 'Zmieniono projekt',
  'project.archive': 'Przeniesiono projekt do archiwum',
  'project.restore': 'Przywrócono projekt z archiwum',
  'project.member.add': 'Dodano osobę do zespołu projektu',
  'project.member.remove': 'Usunięto osobę z zespołu projektu',
  'services.create': 'Dodano umowę',
  'services.update': 'Zmieniono umowę',
  'services.archive': 'Zakończono umowę (archiwum)',
  'services.restore': 'Przywrócono umowę',
  'services.nextPayment.shift': 'Przesunięto termin płatności',
  'maintenance.renewalEmail.send': 'Wysłano przypomnienie o płatności do klienta',
  'maintenance.asset.create': 'Dodano zasób',
  'maintenance.asset.update': 'Zmieniono zasób',
  'maintenance.asset.archive': 'Przeniesiono zasób do archiwum',
  'maintenance.asset.restore': 'Przywrócono zasób',
  'maintenance.component.create': 'Dodano element',
  'maintenance.component.update': 'Zmieniono element',
  'maintenance.supplierRenewal.create': 'Dodano koszt u dostawcy',
  'maintenance.supplierRenewal.update': 'Zmieniono koszt u dostawcy',
  'maintenance.supplierRenewal.archive': 'Usunięto koszt u dostawcy (archiwum)',
  'maintenance.supplierRenewal.restore': 'Przywrócono koszt u dostawcy',
  'maintenance.supplierRenewal.renewed': 'Oznaczono odnowienie u dostawcy',
  'maintenance.payment.create': 'Przygotowano płatność',
  'maintenance.payment.invoiced': 'Oznaczono płatność jako zafakturowaną',
  'maintenance.payment.paid': 'Oznaczono płatność jako opłaconą',
  'maintenance.payment.reopened': 'Cofnięto opłacenie płatności (rozliczenie otwarte do edycji)',
  'maintenance.reminderRule.create': 'Dodano regułę przypomnień',
  'maintenance.reminderRule.update': 'Zmieniono regułę przypomnień',
  // sejf dostępów (historia zasobu; bez sekretów)
  'credential.create': 'Dodano dostęp',
  'credential.update': 'Zmieniono dostęp',
  'credential.secret_change': 'Zmieniono hasło',
  'credential.archive': 'Usunięto dostęp (archiwum)',
  'credential.restore': 'Przywrócono dostęp',
  'vault.reveal': 'Pokazano hasło',
  'vault.copy': 'Skopiowano hasło',
  'vault.rotation.resolve': 'Zamknięto przypomnienie o zmianie hasła',
};

/** Etykiety pól wg typu obiektu audytu (`object_type`). */
export const HISTORY_FIELD_LABELS: Record<string, Record<string, string>> = {
  task: TASK_FIELD_LABELS,
  client: {
    name: 'Nazwa',
    status: 'Status',
    ownerUserId: 'Opiekun',
    defaultRate: 'Stawka',
    currency: 'Waluta',
    color: 'Kolor',
    company: 'Dane firmy',
    notes: 'Notatki',
    links: 'Odnośniki',
    archivedAt: 'Archiwum',
  },
  contact: {
    name: 'Imię i nazwisko',
    email: 'Email',
    phone: 'Telefon',
    role: 'Rola',
    isPrimary: 'Kontakt główny',
    archivedAt: 'Archiwum',
  },
  project: {
    name: 'Nazwa',
    clientId: 'Klient',
    status: 'Status',
    leadUserId: 'Prowadzi',
    userId: 'Osoba',
    role: 'Rola w projekcie',
    isInternal: 'Projekt wewnętrzny',
    billableDefault: 'Czas domyślnie rozliczany',
    startDate: 'Początek',
    endDate: 'Termin',
    hoursBudgetSeconds: 'Budżet godzin',
    goal: 'Cel',
    scopeMd: 'Zakres',
    repoUrl: 'Repozytorium',
    figmaUrl: 'Figma',
    archivedAt: 'Archiwum',
  },
  service: {
    name: 'Nazwa',
    kind: 'Rodzaj',
    status: 'Status',
    price: 'Cena',
    currency: 'Waluta',
    cycle: 'Cykl płatności',
    nextPaymentDate: 'Następna płatność',
    periodStart: 'Od',
    periodEnd: 'Do',
    billingTerms: 'Warunki rozliczenia',
    descriptionMd: 'Ustalenia',
    hoursPoolSeconds: 'Pula godzin',
    autoClientReminder: 'Automatyczne przypomnienie klienta',
    archivedAt: 'Archiwum',
  },
  hosted_asset: {
    name: 'Nazwa',
    type: 'Typ',
    address: 'Adres',
    status: 'Status',
    clientId: 'Klient',
    projectId: 'Projekt',
    serviceId: 'Umowa',
    provider: 'Dostawca',
    server: 'Serwer',
    tech: 'Technologia',
    repoUrl: 'Repozytorium',
    notesMd: 'Notatki',
    archivedAt: 'Archiwum',
  },
  asset_component: {
    kind: 'Rodzaj',
    description: 'Opis',
    provider: 'Dostawca',
    expiresOn: 'Ważne do',
    included: 'W pakiecie',
  },
  supplier_renewal: {
    provider: 'Dostawca',
    amount: 'Kwota',
    currency: 'Waluta',
    cycle: 'Cykl',
    renewsOn: 'Odnowienie',
    notes: 'Notatka',
    archivedAt: 'Archiwum',
  },
  renewal_payment: {
    status: 'Status',
    amount: 'Kwota',
    dueDate: 'Termin płatności',
    periodStart: 'Okres od',
    periodEnd: 'Okres do',
    paidAt: 'Opłacono',
  },
  reminder_rule: {
    audience: 'Do kogo',
    offsetsDays: 'Kiedy (dni)',
    channel: 'Kanał',
    maxRepeats: 'Powtórzenia',
    enabled: 'Włączona',
  },
  // dostęp z sejfu: tylko pola jawne (hasło nigdy nie trafia do audytu)
  credential: {
    label: 'Nazwa',
    panelUrl: 'Adres panelu',
    login: 'Login',
    externalRef: 'Menedżer haseł',
    note: 'Notatka',
    archivedAt: 'Archiwum',
  },
};

/** Pola z długim tekstem: historia pokazuje tylko „zmieniono” (bez treści). */
export const HISTORY_LONG_TEXT_FIELDS = new Set([
  'descriptionMd',
  'notes',
  'notesMd',
  'scopeMd',
  'goal',
  'billingTerms',
  'company',
  'links',
]);

/** Pola pieniężne: bez flagi „Widzi stawki i kwoty” wartości są ukryte. */
export const HISTORY_MONEY_FIELDS = new Set(['defaultRate', 'price', 'amount']);

/** Biernik pola do zdania „zmieniono …” (jedna zmiana). */
const FIELD_ACCUSATIVE: Record<string, string> = {
  title: 'tytuł',
  name: 'nazwę',
  descriptionMd: 'opis',
  clientId: 'klienta',
  projectId: 'projekt',
  serviceId: 'umowę',
  assigneeUserId: 'wykonawcę',
  ownerUserId: 'opiekuna',
  leadUserId: 'prowadzącego',
  status: 'status',
  priority: 'priorytet',
  dueDate: 'termin',
  endDate: 'termin',
  startDate: 'początek',
  email: 'email',
  phone: 'telefon',
  role: 'rolę',
  defaultRate: 'stawkę',
  price: 'cenę',
  amount: 'kwotę',
  nextPaymentDate: 'termin płatności',
  renewsOn: 'datę odnowienia',
  hoursBudgetSeconds: 'budżet godzin',
  address: 'adres',
  provider: 'dostawcę',
  notes: 'notatki',
  notesMd: 'notatki',
  goal: 'cel',
  scopeMd: 'zakres',
};

const SINGLE_FIELD_ACTIONS = new Set([
  'task.update',
  'task.move',
  'client.update',
  'contact.update',
  'project.update',
  'services.update',
  'maintenance.asset.update',
  'maintenance.component.update',
  'maintenance.supplierRenewal.update',
]);

function lowerFirst(text: string): string {
  return text.charAt(0).toLocaleLowerCase('pl-PL') + text.slice(1);
}

/** Zdanie do wpisu historii: „zmieniono status” (jedna zmiana pola) albo etykieta akcji małą literą. */
export function historyActionText(action: string, changes: readonly { field: string }[]): string {
  const single = changes.length === 1 ? FIELD_ACCUSATIVE[changes[0]?.field ?? ''] : undefined;
  if (single && SINGLE_FIELD_ACTIONS.has(action)) return `zmieniono ${single}`;
  const label = HISTORY_ACTION_LABELS[action];
  if (label) return lowerFirst(label);
  return 'zmieniono rekord';
}

// ── Audit Log w UI (Ustawienia → Audyt) ─────────────────────────────────────

/** Rodzaj aktora (`audit_log.actor_type`). */
export const AUDIT_ACTOR_TYPE_LABELS: Record<AuditActorType, string> = {
  user: 'Osoba',
  ai: 'AI',
  mcp: 'MCP',
  worker: 'Automat',
  system: 'System',
};

/** Grupy filtra „Rodzaj”. */
export const AUDIT_ACTION_GROUP_LABELS: Record<AuditActionGroup, string> = {
  vault: 'Sejf',
  approvals: 'Akceptacje',
  team: 'Zespół i uprawnienia',
  auth: 'Logowanie i konto',
  billing: 'Rozliczenia',
  exports: 'Eksporty i pobrania',
};

/** Powód ukrycia wartości w diffie wpisu. */
export const AUDIT_HIDDEN_REASON_LABELS: Record<AuditHiddenReason, string> = {
  secret: 'ukryte (sekret)',
  long_text: 'zmieniono (długi tekst)',
  money: 'ukryte (kwota)',
  complex: 'zmieniono',
};

/** Typ obiektu audytu (`audit_log.object_type`). */
export const AUDIT_OBJECT_TYPE_LABELS: Record<string, string> = {
  client: 'Klient',
  contact: 'Kontakt',
  project: 'Projekt',
  task: 'Zadanie',
  task_proposal: 'Propozycja zadania',
  service: 'Umowa',
  hosted_asset: 'Zasób',
  asset_component: 'Element zasobu',
  supplier_renewal: 'Koszt u dostawcy',
  renewal_payment: 'Płatność',
  reminder_rule: 'Przypomnienie',
  credential: 'Dostęp (sejf)',
  vault_rotation_reminder: 'Przypomnienie o zmianie hasła',
  time_entry: 'Wpis czasu',
  time_report: 'Raport czasu',
  billing_draft: 'Zestawienie',
  knowledge_item: 'Wiedza',
  meeting: 'Spotkanie',
  offer: 'Oferta',
  file: 'Plik',
  approval: 'Akceptacja',
  event: 'Zdarzenie',
  inbox_item: 'Inbox',
  notification: 'Powiadomienie',
  push_subscription: 'Urządzenie (powiadomienia)',
  user_settings: 'Ustawienia osoby',
  membership: 'Osoba w zespole',
  invitation: 'Zaproszenie',
  access_grant: 'Dostęp do rekordu',
  session: 'Sesja',
  trusted_device: 'Zaufane urządzenie',
  user: 'Konto',
  auth_token: 'Link z maila',
  workspace: 'Workspace',
  context_profile: 'Profil AI',
  memory_proposal: 'Propozycja pamięci AI',
  audit_log: 'Dziennik zdarzeń',
  api_token: 'Token MCP',
  oauth_client: 'Aplikacja konektora',
  // Fala 10 (notatki klienta)
  client_note: 'Notatka klienta',
  // 17.10.2026 (karteczki na „Dziś”)
  quick_note: 'Szybka notatka',
  // Fala 11 (partner)
  partner_settlement: 'Rozliczenie partnera',
  partner_rate: 'Stawka partnera',
};

export function auditObjectTypeLabel(type: string): string {
  return AUDIT_OBJECT_TYPE_LABELS[type] ?? 'Rekord';
}

/** Etykiety akcji dziennika (poza historią rekordów). Nieznana akcja — `auditActionLabel` składa ją z czasownika. */
export const AUDIT_ACTION_LABELS: Record<string, string> = {
  ...HISTORY_ACTION_LABELS,
  // logowanie i konto
  'auth.login': 'Logowanie',
  'auth.logout': 'Wylogowanie',
  // Fala 10: notatki klienta
  'client_note.create': 'Dodano notatkę',
  'client_note.update': 'Zmieniono notatkę',
  'client_note.pin': 'Przypięto notatkę',
  'client_note.unpin': 'Odpięto notatkę',
  'client_note.archive': 'Notatka do archiwum',
  'client_note.restore': 'Przywrócono notatkę',
  // 17.10.2026: szybkie notatki (karteczki na „Dziś”; dziennik bez treści — notatka jest prywatna)
  'quick_note.create': 'Dodano szybką notatkę',
  'quick_note.update': 'Zmieniono szybką notatkę',
  'quick_note.complete': 'Odhaczono szybką notatkę',
  'quick_note.reopen': 'Przywrócono szybką notatkę',
  'quick_note.archive': 'Szybka notatka do archiwum',
  'quick_note.to_task': 'Szybka notatka zamieniona w zadanie',
  'auth.password.change': 'Zmiana hasła',
  'auth.password.reset': 'Nowe hasło z linku w mailu',
  'auth.password.reset_requested': 'Prośba o link do zmiany hasła',
  'auth.session.revoke': 'Wylogowanie urządzenia',
  'auth.session.revoke_others': 'Wylogowanie pozostałych urządzeń',
  'auth.step_up': 'Potwierdzenie tożsamości (hasło albo kod z maila)',
  'auth.account.locked': 'Blokada konta po nieudanych próbach',
  'auth.workspace.switch': 'Przełączenie workspace’u',
  'auth.recovery.reset_password': 'Odzyskanie dostępu z konsoli: nowe hasło',
  'auth.recovery.change_email': 'Odzyskanie dostępu z konsoli: zmiana emaila',
  'auth.recovery.unlock': 'Odzyskanie dostępu z konsoli: odblokowanie konta',
  // drugi składnik: kod z maila (Fala 7); 'auth.step_up' obejmuje też potwierdzenie kodem (meta.method)
  'auth.second_factor.challenge': 'Hasło poprawne, wysłano kod logowania',
  'auth.second_factor.resend': 'Ponownie wysłano kod logowania',
  'auth.second_factor.code_sent': 'Wysłano kod potwierdzenia',
  'auth.second_factor.enable': 'Włączono kod z maila przy logowaniu',
  'auth.second_factor.disable': 'Wyłączono kod z maila przy logowaniu',
  'auth.trusted_device.add': 'Zaufane urządzenie (30 dni)',
  'auth.trusted_device.revoke': 'Odwołano zaufane urządzenie',
  'auth.trusted_device.revoke_all': 'Odwołano wszystkie zaufane urządzenia',
  'auth.recovery.reset_second_factor': 'Odzyskanie dostępu z konsoli: kod z maila od zera',
  'system.outbox.requeue': 'Ponowienie zadań w tle (konsola)',
  'system.bootstrap_owner': 'Pierwsze uruchomienie: właściciel i workspace (konsola)',
  'system.create_workspace': 'Nowy workspace (konsola)',
  // 9.10: konektor Claude (OAuth 2.1 dla MCP)
  'oauth.consent_granted': 'Zgoda na połączenie konektora',
  'oauth.consent_denied': 'Odmowa połączenia konektora',
  'oauth.token_issued': 'Konektor połączony (wydano token)',
  'oauth.token_refreshed': 'Konektor odświeżył token',
  'oauth.token_revoked': 'Konektor odłączony',
  'oauth.code_reuse_detected': 'Ponowne użycie kodu konektora — połączenie cofnięte',
  'oauth.refresh_reuse_detected': 'Ponowne użycie tokenu odświeżania — połączenie cofnięte',
  // Fala 11: partner i kilka workspace'ów
  'user.primary_workspace': 'Zmieniono główny workspace',
  'partner_settlement.close': 'Zamknięto rozliczenie partnera',
  'partner_settlement.reopen': 'Otwarto ponownie rozliczenie partnera',
  'partner_rate.default': 'Zmieniono domyślną stawkę partnera',
  'partner_rate.project': 'Zmieniono stawkę partnera w projekcie',
  // zespół i uprawnienia
  'team.invitation.create': 'Zaproszono osobę',
  'team.invitation.resend': 'Wysłano zaproszenie ponownie',
  'team.invitation.cancel': 'Anulowano zaproszenie',
  'team.invitation.accept': 'Przyjęto zaproszenie',
  'team.member.role_change': 'Zmieniono rolę',
  'team.member.permissions_update': 'Zmieniono uprawnienia',
  'team.member.template_apply': 'Zastosowano szablon uprawnień',
  'team.member.grant_remove': 'Odebrano dostęp',
  'team.member.deactivate': 'Wyłączono dostęp osoby',
  'team.member.reactivate': 'Włączono dostęp osoby',
  'access_grant.create': 'Nadano dostęp do rekordu',
  // sejf
  'vault.reveal': 'Podgląd hasła z sejfu',
  'vault.copy': 'Skopiowano hasło z sejfu',
  'vault.rotation.resolve': 'Zamknięto przypomnienie o zmianie hasła',
  'credential.create': 'Dodano dostęp do sejfu',
  'credential.update': 'Zmieniono dostęp w sejfie',
  'credential.secret_change': 'Zmieniono hasło w sejfie',
  'credential.archive': 'Usunięto dostęp z sejfu (archiwum)',
  'credential.restore': 'Przywrócono dostęp w sejfie',
  // akceptacje
  'approval.request': 'Akcja czeka na akceptację',
  'approval.resolve': 'Rozstrzygnięto akceptację',
  'approval.approve': 'Zatwierdzono akcję',
  'approval.reject': 'Odrzucono akcję',
  'approval.edit': 'Zmieniono treść akcji',
  'approval.withdraw': 'Wycofano akceptację',
  'approval.expire': 'Akceptacja wygasła',
  'approval.execute': 'Wykonano akcję',
  'approval.execute.start': 'Rozpoczęto wykonanie akcji',
  'approval.execute.succeeded': 'Akcja wykonana',
  'approval.execute.failed': 'Błąd wykonania akcji',
  'approval.reconcile': 'Sprawdzono zawieszone wykonanie',
  'approval.fail': 'Błąd wykonania akcji',
  'approval.succeed': 'Akcja wykonana',
  'approval.retry': 'Ponowiono wykonanie akcji',
  'approval.settle': 'Akceptacja rozstrzygnięta w module',
  // czas i rozliczenia
  'time_entry.create': 'Dodano wpis czasu',
  'time_entry.update': 'Zmieniono wpis czasu',
  'time_entry.archive': 'Usunięto wpis czasu',
  'time_entry.restore': 'Przywrócono wpis czasu',
  'time.export': 'Eksport raportu czasu',
  'billing.draft.create': 'Utworzono zestawienie',
  'billing.draft.addEntries': 'Dodano wpisy do zestawienia',
  'billing.draft.removeEntries': 'Usunięto wpisy z zestawienia',
  'billing.draft.addLine': 'Dodano pozycję stałą',
  'billing.draft.removeLine': 'Usunięto pozycję z zestawienia',
  'billing.draft.update': 'Zmieniono zestawienie',
  'billing.draft.submit': 'Wysłano zestawienie do wystawienia faktury',
  'billing.draft.reopen': 'Zestawienie wróciło do edycji',
  'billing.draft.cancel': 'Anulowano zestawienie',
  'billing.draft.issue': 'Wystawiono fakturę',
  'billing.draft.issueFailed': 'Błąd wystawienia faktury',
  'billing.draft.confirmNotIssued': 'Potwierdzono ręcznie: faktura nie powstała',
  'billing.draft.paid': 'Oznaczono zestawienie jako opłacone',
  'billing.draft.markIssued': 'Oznaczono fakturę jako wystawioną (numer z systemu faktur)',
  'billing.draft.markSent': 'Oznaczono rozliczenie jako wysłane klientowi',
  'billing.draft.updateLine': 'Zmieniono pozycję zestawienia',
  'billing.draft.reopenPaid': 'Rozliczenie otwarte do edycji (usunięto opłacenie)',
  'billing.draft.export': 'Eksport zestawienia',
  // eksporty i pliki
  'export.client_md': 'Eksport CLIENT.md',
  'export.project_md': 'Eksport PROJECT.md',
  'audit.export': 'Eksport dziennika zdarzeń',
  'file.upload': 'Dodano plik',
  'file.download': 'Pobrano plik',
  'file.archive': 'Usunięto plik (archiwum)',
  // wiedza, spotkania, oferty
  'knowledge.create': 'Dodano wpis wiedzy',
  'knowledge.update': 'Zmieniono wpis wiedzy',
  'knowledge.archive': 'Usunięto wpis wiedzy (archiwum)',
  'knowledge.restore': 'Przywrócono wpis wiedzy',
  'knowledge.version.restore': 'Przywrócono wersję wpisu wiedzy',
  'meeting.create': 'Dodano spotkanie',
  'meeting.update': 'Zmieniono spotkanie',
  'meeting.archive': 'Usunięto spotkanie (archiwum)',
  'meeting.restore': 'Przywrócono spotkanie',
  'meeting.process.request': 'Zlecono przetworzenie spotkania przez AI',
  'meeting.process.done': 'AI przetworzyło spotkanie',
  'meeting.process.fail': 'Błąd przetwarzania spotkania',
  'meeting.decisions.decide': 'Rozstrzygnięto ustalenia ze spotkania',
  'task_proposal.accept': 'Przyjęto propozycję zadania',
  'task_proposal.reject': 'Odrzucono propozycję zadania',
  'offer.create': 'Utworzono ofertę',
  'offer.update': 'Zmieniono ofertę',
  'offer.version.create': 'Nowa wersja oferty',
  'offer.submit': 'Przekazano ofertę właścicielowi',
  'offer.backToDraft': 'Oferta wróciła do edycji',
  'offer.send.request': 'Poproszono o wysyłkę oferty',
  'offer.send': 'Wysłano ofertę',
  'offer.accept': 'Oferta przyjęta',
  'offer.reject': 'Oferta odrzucona',
  'offer.terms.apply': 'Zastosowano zmianę warunków oferty',
  // AI, zdarzenia, powiadomienia, utrzymanie
  'context.profile.save': 'Zapisano profil AI',
  'context.memory.propose': 'Zaproponowano zmianę pamięci AI',
  'context.memory.accept': 'Przyjęto zmianę pamięci AI',
  'context.memory.reject': 'Odrzucono zmianę pamięci AI',
  'event.reclassify': 'Zmieniono ważność zdarzenia',
  'event.simulate': 'Zdarzenie testowe (symulacja)',
  'inbox.done': 'Oznaczono jako zrobione w Inboxie',
  'inbox.restore': 'Przywrócono element Inboxu',
  'inbox.snooze': 'Odłożono element Inboxu',
  'inbox.unsnooze': 'Przywrócono odłożony element Inboxu',
  'notifications.settings.update': 'Zmieniono ustawienia powiadomień',
  'notifications.device.add': 'Włączono powiadomienia na urządzeniu',
  'notifications.device.update': 'Zmieniono urządzenie powiadomień',
  'notifications.device.remove': 'Usunięto urządzenie z powiadomień',
  'notifications.test': 'Wysłano powiadomienie testowe',
  'test.mail.send': 'Wysłano mail testowy',
  'maintenance.reminders.run': 'Uruchomiono przypomnienia o płatnościach',
};

/** Czasownik ostatniego członu akcji (dla akcji spoza listy, np. dodanych przez nowy moduł). */
const AUDIT_ACTION_VERBS: Record<string, string> = {
  create: 'Dodano',
  add: 'Dodano',
  update: 'Zmieniono',
  edit: 'Zmieniono',
  save: 'Zapisano',
  archive: 'Archiwum',
  restore: 'Przywrócono',
  delete: 'Usunięto',
  remove: 'Usunięto',
  approve: 'Zatwierdzono',
  reject: 'Odrzucono',
  accept: 'Przyjęto',
  send: 'Wysłano',
  export: 'Eksport',
  download: 'Pobrano',
  upload: 'Dodano plik',
  execute: 'Wykonano',
  fail: 'Błąd',
  failed: 'Błąd',
  expire: 'Wygasło',
  revoke: 'Cofnięto',
  reveal: 'Podgląd',
  copy: 'Skopiowano',
};

/** Etykieta akcji dziennika; nieznana: „Zmieniono · Akceptacja” (czasownik ostatniego członu + typ obiektu). */
export function auditActionLabel(action: string, objectType?: string | null): string {
  const known = AUDIT_ACTION_LABELS[action];
  if (known) return known;
  const last = action.split('.').pop() ?? '';
  const verb = AUDIT_ACTION_VERBS[last] ?? 'Działanie';
  return objectType ? `${verb} · ${auditObjectTypeLabel(objectType)}` : verb;
}

/** Pola dziennika: etykiety per typ obiektu (historia rekordów + typy spoza historii). */
export const AUDIT_FIELD_LABELS: Record<string, Record<string, string>> = {
  ...HISTORY_FIELD_LABELS,
  time_entry: {
    startedAt: 'Początek',
    endedAt: 'Koniec',
    durationSeconds: 'Czas',
    description: 'Opis',
    userId: 'Osoba',
    clientId: 'Klient',
    projectId: 'Projekt',
    serviceId: 'Umowa',
    taskId: 'Zadanie',
    billable: 'Rozliczany',
    rate: 'Stawka',
    amount: 'Kwota',
    currency: 'Waluta',
    source: 'Źródło',
    archivedAt: 'Usunięty',
  },
  billing_draft: {
    status: 'Status',
    clientId: 'Klient',
    label: 'Nazwa',
    periodFrom: 'Okres od',
    periodTo: 'Okres do',
    totalSeconds: 'Czas',
    totalNet: 'Kwota netto',
    dueDate: 'Termin płatności',
    paidAt: 'Opłacono',
    paymentSource: 'Źródło wpłaty',
    externalNumber: 'Numer faktury',
  },
  knowledge_item: {
    title: 'Tytuł',
    kind: 'Rodzaj',
    scope: 'Zakres',
    clientId: 'Klient',
    projectId: 'Projekt',
    decidedOn: 'Data decyzji',
    url: 'Adres',
  },
  meeting: {
    title: 'Tytuł',
    startsAt: 'Początek',
    endsAt: 'Koniec',
    location: 'Miejsce',
    clientId: 'Klient',
    projectId: 'Projekt',
    aiStatus: 'Stan AI',
    transcriptText: 'Transkrypcja',
  },
  offer: {
    title: 'Tytuł',
    status: 'Status',
    clientId: 'Klient',
    number: 'Numer',
    currentVersion: 'Wersja',
    versionNo: 'Wersja',
    content: 'Treść',
    sentTo: 'Odbiorcy',
  },
  file: {
    filename: 'Nazwa pliku',
    mime: 'Typ pliku',
    sizeBytes: 'Rozmiar (bajty)',
    clientId: 'Klient',
    projectId: 'Projekt',
  },
  membership: {
    role: 'Rola',
    status: 'Status',
    template: 'Szablon',
    dataScope: 'Zakres danych',
    modules: 'Moduły',
    flags: 'Uprawnienia specjalne',
    aiMonthlyLimitPln: 'Limit AI (zł / mies.)',
    grants: 'Dostępy',
    approvesAi: 'Zatwierdza akcje AI',
  },
  invitation: {
    email: 'Email',
    role: 'Rola',
    template: 'Szablon',
    dataScope: 'Zakres danych',
    modules: 'Moduły',
    flags: 'Uprawnienia specjalne',
    aiMonthlyLimitPln: 'Limit AI (zł / mies.)',
    grants: 'Dostępy',
    expiresAt: 'Ważne do',
  },
  approval: {
    type: 'Rodzaj akcji',
    status: 'Status',
    summary: 'Opis',
    note: 'Notatka',
    decisionNote: 'Notatka decyzji',
    reason: 'Powód',
  },
  credential: {
    label: 'Nazwa',
    login: 'Login',
    panelUrl: 'Adres panelu',
    secret: 'Hasło',
    externalRef: 'Menedżer haseł',
    note: 'Notatka',
    assetId: 'Zasób',
    secretChangedAt: 'Zmiana hasła',
    archivedAt: 'Archiwum',
  },
};

/** Pola wspólne (gdy typ obiektu nie ma własnej etykiety pola). */
export const AUDIT_COMMON_FIELD_LABELS: Record<string, string> = {
  name: 'Nazwa',
  title: 'Tytuł',
  label: 'Nazwa',
  status: 'Status',
  type: 'Rodzaj',
  kind: 'Rodzaj',
  clientId: 'Klient',
  projectId: 'Projekt',
  serviceId: 'Umowa',
  taskId: 'Zadanie',
  assetId: 'Zasób',
  userId: 'Osoba',
  email: 'Email',
  role: 'Rola',
  description: 'Opis',
  notes: 'Notatka',
  amount: 'Kwota',
  rate: 'Stawka',
  price: 'Cena',
  currency: 'Waluta',
  archivedAt: 'Archiwum',
  expiresAt: 'Ważne do',
  dueDate: 'Termin',
  count: 'Liczba',
  reason: 'Powód',
  decision: 'Decyzja',
  enabled: 'Włączone',
  severity: 'Ważność',
  purpose: 'Cel',
  number: 'Numer',
  origin: 'Pochodzenie',
  originRef: 'Źródło',
  source: 'Źródło',
  panelUrl: 'Adres panelu',
  login: 'Login',
  note: 'Notatka',
  externalRef: 'Menedżer haseł',
  secretChangedAt: 'Zmiana hasła',
};

/** Pola techniczne, których dziennik nie pokazuje w diffie (id, wersje, znaczniki, skróty treści). */
export const AUDIT_SKIPPED_FIELDS = new Set([
  'id',
  'workspaceId',
  'version',
  'createdAt',
  'updatedAt',
  'createdBy',
  'position',
  'anchorDay',
  'sha256',
  'versionHash',
  'payloadHash',
  'tokenHash',
  'externalId',
  'emailConflictResolvedBy',
  'secretCiphertext',
  'secretIv',
  'secretTag',
  'keyVersion',
  'storageKey',
]);

/** Długie pola (dziennik pokazuje „zmieniono”, bez treści). */
export const AUDIT_LONG_TEXT_FIELDS = new Set([...HISTORY_LONG_TEXT_FIELDS, 'transcriptText', 'content', 'bodyMd']);

/** Pola pieniężne: bez „Widzi stawki i kwoty” ukryte. */
export const AUDIT_MONEY_FIELDS = new Set([...HISTORY_MONEY_FIELDS, 'rate', 'totalNet', 'unitPrice', 'totalGross']);
